import React from 'react';

import { IconPhone, IconUserEdit, IconUsers } from '@tabler/icons-react';
import { Contact01Icon, Database01Icon, GitbookIcon, Mail01Icon, TextIndentMoreIcon } from 'hugeicons-react';
import { UsageTable } from 'pages/Settings/SettingTabs/UsageSettingsTab/UsageTable';
import { useTranslation } from 'react-i18next';
import { OrganisationUsage, UsageProgress } from 'shared/interfaces/Usage';
import { formatFileSizeLabel, formatMinutesLabelFromSeconds, formatNumberWithSpacesLabel } from 'shared/utils/format';

interface UsageProps {
  usage: OrganisationUsage;
}

export function OrganisationUsageSummary({ usage }: UsageProps) {
  const { t } = useTranslation();

  const progressBars: UsageProgress[] = [
    {
      icon: <IconUserEdit />,
      label: t('USAGE.ADMINS'),
      progressLabel: formatNumberWithSpacesLabel(usage.adminsCount, usage.limits.adminsLimit),
      value: usage.adminsCount,
      max: usage.limits.adminsLimit
    },
    {
      icon: <IconUsers />,
      label: t('USAGE.MEMBERS'),
      progressLabel: formatNumberWithSpacesLabel(usage.membersCount, usage.limits.membersLimit),
      value: usage.membersCount,
      max: usage.limits.membersLimit
    },
    {
      icon: <GitbookIcon size={20} />,
      label: t('USAGE.EMBEDDING_TOKENS'),
      progressLabel: formatNumberWithSpacesLabel(usage.embeddingTokenCount, usage.limits.embeddingTokenLimit),
      value: usage.embeddingTokenCount,
      max: usage.limits.embeddingTokenLimit
    },
    {
      icon: <Mail01Icon size={20} />,
      label: t('USAGE.USAGE_TOKENS'),
      progressLabel: formatNumberWithSpacesLabel(usage.usageTokenCount, usage.limits.usageTokenLimit),
      value: usage.usageTokenCount,
      max: usage.limits.usageTokenLimit
    },
    {
      icon: <Database01Icon size={20} />,
      label: t('USAGE.TOTAL_FILE_STORAGE'),
      progressLabel: formatFileSizeLabel(usage.totalFileStorageCount, usage.limits.totalFileStorageLimit),
      value: usage.totalFileStorageCount,
      max: usage.limits.totalFileStorageLimit
    },
    {
      icon: <TextIndentMoreIcon size={20} />,
      label: t('USAGE.TOTAL_VIDEOS_TIME'),
      progressLabel: formatMinutesLabelFromSeconds(usage.totalVideosTimeCount, usage.limits.totalVideosTimeLimit),
      value: usage.totalVideosTimeCount,
      max: usage.limits.totalVideosTimeLimit
    },
    {
      icon: <IconPhone />,
      label: t('USAGE.TOTAL_VOICE_CALL_MINUTES'),
      progressLabel: formatMinutesLabelFromSeconds(
        usage.totalVoiceCallSecondsCount,
        usage.limits.totalVoiceCallSecondsLimit
      ),
      value: usage.totalVoiceCallSecondsCount,
      max: usage.limits.totalVoiceCallSecondsLimit
    },
    {
      icon: <Contact01Icon size={20} />,
      label: t('USAGE.TOTAL_PHONE_NUMBERS'),
      progressLabel: formatNumberWithSpacesLabel(
        usage.totalProvidedPhoneNumbersCount,
        usage.limits.totalPhoneNumbersLimit
      ),
      value: usage.totalProvidedPhoneNumbersCount,
      max: usage.limits.totalPhoneNumbersLimit
    }
  ];

  return <UsageTable progressItems={progressBars} />;
}
